import { studyQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { PaginatedStudiesResponse, Study, StudySearchRequest, StudyUpdateResponse } from '@/types';
import type { components } from '@/types/api';

/**
 * Search studies using the POST /orgs/{org_id}/studies/search endpoint
 *
 * @param orgId - The organization ID
 * @param searchRequest - The search request body containing pagination, sorting, and filters
 * @returns Promise resolving to paginated studies response
 *
 * This function uses the search endpoint which supports:
 * - Pagination (skip/limit, max limit: 500)
 * - Sorting by multiple fields
 * - Filtering by name, description, created_by, dates, etc.
 *
 * **Important:** The API enforces a maximum limit of 500 records per request.
 * For larger datasets, use multiple requests with skip/limit pagination.
 *
 * @example
 * ```typescript
 * const results = await searchStudies('org-123', {
 *   skip: 0,
 *   limit: 100, // Max 500
 *   sort_by: 'created_at',
 *   sort_order: 'desc',
 *   filters: {
 *     name: {
 *       conditions: [{ operator: 'contains', value: 'test' }],
 *       logic: 'and'
 *     }
 *   }
 * });
 * ```
 */
export async function searchStudies(
  orgId: string,
  searchRequest: StudySearchRequest,
): Promise<PaginatedStudiesResponse> {
  const result = await apiClient.post<PaginatedStudiesResponse>(
    studyQueryKeys.endpoints.listSearch(orgId),
    searchRequest,
  );

  // Search endpoint should always return data, but handle null case defensively
  if (result === null) {
    throw new Error('Search endpoint returned no content (unexpected 204 response)');
  }

  return result;
}

/**
 * Fetch a single study by ID
 *
 * @param orgId - The organization ID
 * @param studyId - The study ID
 * @returns Promise resolving to the study data
 *
 * @throws {ApiError} If the request fails or the study is not found
 *
 * @example
 * ```typescript
 * const study = await fetchStudy('org-123', 'study-456');
 * console.log(study.name);
 * ```
 */
export async function fetchStudy(orgId: string, studyId: string): Promise<Study> {
  const result = await apiClient.get<Study>(studyQueryKeys.endpoints.detail(orgId, studyId));

  if (result === null) {
    throw new Error(`Study ${studyId} not found`);
  }

  return result;
}

/**
 * Update a study's name and/or description (PATCH semantics — only provided fields are updated).
 * Only study owners can call this endpoint.
 *
 * @param orgId - The organization ID
 * @param studyId - The study ID to update
 * @param body - Fields to update (name and/or description)
 * @returns Promise resolving to the updated study response
 */
export async function updateStudy(
  orgId: string,
  studyId: string,
  body: components['schemas']['StudyUpdateRequest'],
): Promise<StudyUpdateResponse> {
  const result = await apiClient.patch<StudyUpdateResponse>(studyQueryKeys.endpoints.detail(orgId, studyId), body);

  if (result === null) {
    throw new Error('Study update returned no content (unexpected 204 response)');
  }

  return result;
}
